import {
  PracticeAddressFragment,
  BookingBar,
  PracticeTag,
  RawSearchResultFragment,
  SearchAvailableAppointmentFragment,
} from '../common/schema/hannibal/types.generated';
import { filterNulls } from '../common/util/filterNulls';
import {
  SearchResultAddress,
  SearchResultAppointment,
  SearchResultPractice,
  SearchResultPractitioner,
  SearchResultProfile,
} from './types';

const parseAppointment = (
  it: SearchAvailableAppointmentFragment,
  practiceId: string | undefined,
): SearchResultAppointment => ({
  date: it.date ?? undefined,
  time: it.time ?? undefined,
  practiceId: practiceId,
  appointmentPractitionerId: it.appointmentPractitionerId ?? undefined,
  specialty: it.specialty ?? undefined,
});

export const parseProfile = (result: RawSearchResultFragment): SearchResultProfile[] => {
  const { profile, appointmentSummary } = result;

  if (!profile) return [];

  switch (profile.__typename) {
    case 'Practice': {
      const practice: SearchResultPractice = {
        type: 'practice',
        featured: profile.featured,
        id: profile.id,
        name: profile.name,
        tagline: profile.tagline ?? '',
        appointmentSummary: appointmentSummary?.availableAppointment
          ? {
              firstAppointment: parseAppointment(
                appointmentSummary.availableAppointment,
                profile.id,
              ),
              count: appointmentSummary.appointmentCountOnSameDay,
            }
          : null,
        specialties: profile.specialties?.map(it => it.name) ?? [],
        tags: profile.tags?.map(it => it) ?? [],
        address: parseAddress(profile.address),
        rating: profile.rating
          ? {
              count: profile.rating.count,
              rating: profile.rating.rating,
              disabled: profile.rating.disabled,
              disabledReason: profile.rating.disabledReason ?? undefined,
            }
          : undefined,
        image:
          profile.searchImage?.link ?? profile.images?.[0]?.link ?? profile.logo?.link ?? undefined,
        logo: profile.logo?.link ?? undefined,
        practitioners: (profile.practitioners || []).map(it => ({
          id: it.id,
          image: it.profilePhoto?.link ?? undefined,
          title: it.title ?? undefined,
          firstName: it.firstName ?? undefined,
          lastName: it.lastName ?? undefined,
          url: it.url,
        })),
        phoneNumber: profile.phoneNumber ?? undefined,
        interests: (profile.interests || []).map(it => it.name),
        preferredProviders: (profile.preferredProviders || []).map(it => it.name),
        preferredProviderPrograms: profile.preferredProviderPrograms,
        url: profile.url,
        bookingBar: appointmentSummary?.bookingMethod ?? BookingBar.Phone,
        timezone: profile?.timezone ?? undefined,
        treatments: (profile.treatments || []).map(it => ({
          price: it.price,
          importedAt: it.importedAt || undefined,
          treatment: {
            code: it.treatment.code,
            codeType: it.treatment.codeType,
            name: it.treatment.name,
          },
        })),
        rankingInfo: profile.rankingInfo ?? undefined,
      };

      return [practice];
    }

    case 'Practitioner': {
      const practitioner: SearchResultPractitioner = {
        type: 'practitioner',
        bulkBilling: profile.practices.some(it => it.tags.includes(PracticeTag.BulkBilling)),
        id: profile.id,
        appointmentSummary: appointmentSummary?.availableAppointment
          ? {
              firstAppointment: parseAppointment(
                appointmentSummary.availableAppointment,
                profile.primaryPractice?.id,
              ),
              count: appointmentSummary.appointmentCountOnSameDay,
            }
          : null,
        title: profile.title ?? undefined,
        firstName: profile.firstName ?? undefined,
        lastName: profile.lastName ?? undefined,
        gender: profile.gender ?? undefined,
        practices: filterNulls(
          profile.practices.map(it => {
            const address = parseAddress(it.address);
            return address
              ? { id: it.id, phoneNumber: it.phoneNumber ?? undefined, address }
              : null;
          }),
        ),
        profilePhoto: profile.profilePhoto?.link ?? undefined,
        specialties: (profile.specialties || []).map(it => it.specialist),
        primaryPractice: profile.primaryPractice
          ? {
              id: profile.primaryPractice.id,
              phoneNumber: profile.primaryPractice.phoneNumber ?? undefined,
              address: parseAddress(profile.primaryPractice.address),
            }
          : undefined,
        interests: (profile.interests || []).map(it => it.name),
        url: profile.url,
        bookingBar: appointmentSummary?.bookingMethod ?? BookingBar.Phone,
        rankingInfo: profile.rankingInfo ?? undefined,
      };

      return [practitioner];
    }
  }
};

const parseAddress = (
  address: PracticeAddressFragment['address'] | undefined,
): SearchResultAddress | undefined =>
  address
    ? {
        longitude: address.longitude,
        latitude: address.latitude,
        state: address.state ?? undefined,
        street: address.street ?? undefined,
        streetNumber: address.streetNumber ?? undefined,
        suburb: address.suburb,
        suite: address.suite ?? undefined,
      }
    : undefined;
